import { QueryReturnValue } from '@packages/utils';
import {
  signIn,
  confirmSignIn,
  signOut,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  updatePassword,
  confirmResetPassword,
  getCurrentUser,
  fetchUserAttributes,
  fetchMFAPreference,
} from 'aws-amplify/auth';
import {
  resetPasswordConverter,
  signInAmplifyConverter,
} from 'data/convertors/auth.converters';
import {
  EPreferredMFATypes,
  EVerifyTotpToken,
  ICurrentAuthUser,
  TCurrentAuthUserUI,
  TChangePassword,
  TChangePasswordUI,
  TCompleteNewPassword,
  TCompleteNewPasswordUI,
  TConfirmSignInDB,
  TConfirmSignInUI,
  TResetPasswordUI,
  TSetPreferredMFAUI,
  TSetupTOTPDB,
  TSignInAmplifyUI,
  TVerifyTotpTokenDB,
  TVerifyTotpTokenUI,
  UserEmailAttributes,
} from 'data/types/amplify.types';
import { authSplitApi } from 'redux/helpers/slice.helpers';

// Amplify v6 throws Error subclasses that expose `name`/`message` (no `code`).
// Keep the legacy { name, code, message } error shape the UI relies on.
type TAmplifyV6Error = { name?: string; message?: string; code?: string };
const buildAuthError = (e: unknown, username?: string) => {
  const error = (e || {}) as TAmplifyV6Error;
  const code = error.code ?? error.name ?? 'UnknownError';
  return {
    error: {
      data: {
        name: error.name,
        code,
        message: error.message,
        ...(username !== undefined ? { username } : {}),
      },
      status: code,
    },
  };
};

// Build the app's internal auth-user representation from v6 primitives
// (v6 no longer hands back a CognitoUser object).
const buildAuthUser = async (): Promise<TCurrentAuthUserUI> => {
  const { username } = await getCurrentUser();
  const attributes = await fetchUserAttributes();
  let preferredMFA = EPreferredMFATypes.NOMFA;
  try {
    const mfa = await fetchMFAPreference();
    if (mfa.preferred === 'TOTP') {
      preferredMFA = EPreferredMFATypes.TOTP;
    }
  } catch {
    // fetchMFAPreference can fail for federated/edge cases — default to NOMFA
  }
  return {
    username,
    name: attributes.name,
    email: attributes.email,
    attributes: attributes as unknown as UserEmailAttributes,
    preferredMFA,
  };
};

export const authApi = authSplitApi('auth').injectEndpoints({
  endpoints: build => ({
    signIn: build.mutation<TCurrentAuthUserUI, TSignInAmplifyUI>({
      // @ts-ignore
      async queryFn(arg) {
        try {
          const { username, password } = signInAmplifyConverter.toDb(arg);
          const { isSignedIn, nextStep } = await signIn({ username, password });
          let user: TCurrentAuthUserUI;
          if (isSignedIn) {
            user = await buildAuthUser();
          } else if (
            nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
          ) {
            user = {
              username,
              email: arg.email,
              challengeName: 'NEW_PASSWORD_REQUIRED',
            };
          } else if (
            nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
            nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP'
          ) {
            user = {
              username,
              email: arg.email,
              challengeName: 'SOFTWARE_TOKEN_MFA',
            };
          } else {
            user = { username, email: arg.email };
          }
          localStorage.setItem('amplifyUser', JSON.stringify(user));
          return {
            data: user,
          } as QueryReturnValue<ICurrentAuthUser>;
        } catch (e) {
          return buildAuthError(e, arg.email);
        }
      },
    }),
    // confirms sign in after the 2FA code is submitted in the login flow
    confirmSignIn: build.mutation<TConfirmSignInDB, TConfirmSignInUI>({
      // @ts-ignore
      async queryFn(arg) {
        try {
          const { isSignedIn } = await confirmSignIn({
            challengeResponse: arg.code,
          });
          if (!isSignedIn) {
            return buildAuthError({
              name: 'NotAuthorizedException',
              message: 'Sign in could not be completed.',
            });
          }
          const user = await buildAuthUser();
          user.preferredMFA = EPreferredMFATypes.TOTP;
          localStorage.setItem('amplifyUser', JSON.stringify(user));
          return {
            data: user,
          } as QueryReturnValue<ICurrentAuthUser>;
        } catch (e) {
          return buildAuthError(e);
        }
      },
    }),
    // returns the shared secret used to activate 2FA in an authenticator app
    getTOTPCode: build.mutation<TSetupTOTPDB, void>({
      // @ts-ignore
      async queryFn() {
        try {
          const totp = await setUpTOTP();
          return { data: totp.sharedSecret };
        } catch (e) {
          return { error: e };
        }
      },
    }),
    // verifies the TOTP setup using the code from the authenticator app
    verifyTotpToken: build.mutation<TVerifyTotpTokenDB, TVerifyTotpTokenUI>({
      // @ts-ignore
      async queryFn(arg) {
        try {
          await verifyTOTPSetup({ code: arg.challengeAnswer });
          return { data: { Status: EVerifyTotpToken.SUCCESS } };
        } catch (e) {
          return buildAuthError(e);
        }
      },
    }),
    // updates the preferred MFA type, which determines if 2FA is on or off
    setPreferredMFA: build.mutation<void, TSetPreferredMFAUI>({
      // @ts-ignore
      async queryFn(arg) {
        try {
          const enable = String(arg.MFAMethod) !== EPreferredMFATypes.NOMFA;
          await updateMFAPreference({
            totp: enable ? 'PREFERRED' : 'DISABLED',
          });
          return { data: undefined };
        } catch (e) {
          return buildAuthError(e);
        }
      },
    }),
    signOut: build.mutation<void, void>({
      // @ts-ignore
      async queryFn() {
        try {
          await signOut();
          return { data: null };
        } catch (e) {
          return { error: e };
        }
      },
    }),
    changePassword: build.mutation<TChangePassword, TChangePasswordUI>({
      // @ts-ignore
      async queryFn(arg) {
        try {
          await updatePassword({
            oldPassword: arg.existing,
            newPassword: arg.password,
          });
          return { data: null };
        } catch (e) {
          return buildAuthError(e);
        }
      },
    }),
    setNewPassword: build.mutation<
      TCompleteNewPassword,
      TCompleteNewPasswordUI
    >({
      // @ts-ignore
      async queryFn(arg) {
        try {
          const { isSignedIn, nextStep } = await confirmSignIn({
            challengeResponse: arg.newPassword,
          });
          let user: TCurrentAuthUserUI;
          if (isSignedIn) {
            user = await buildAuthUser();
          } else if (
            nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
            nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP'
          ) {
            user = { challengeName: 'SOFTWARE_TOKEN_MFA' };
          } else {
            user = {};
          }
          localStorage.setItem('amplifyUser', JSON.stringify(user));
          return { data: user };
        } catch (e) {
          return buildAuthError(e);
        }
      },
    }),
    resetPasswordAmplify: build.mutation<void, TResetPasswordUI>({
      // @ts-ignore
      async queryFn(data) {
        const { username, password, code } = resetPasswordConverter.toDb(data);
        try {
          await confirmResetPassword({
            username,
            confirmationCode: code,
            newPassword: password,
          });
          return { data: null };
        } catch (e) {
          return buildAuthError(e);
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useSignInMutation,
  useSignOutMutation,
  useSetNewPasswordMutation,
  useChangePasswordMutation,
  useResetPasswordAmplifyMutation,
  useGetTOTPCodeMutation,
  useConfirmSignInMutation,
  useSetPreferredMFAMutation,
  useVerifyTotpTokenMutation,
} = authApi;

export const currentAmplifyAuthUser = async (): Promise<TCurrentAuthUserUI> => {
  const { username } = await getCurrentUser();
  const attributes = await fetchUserAttributes();
  return {
    username,
    name: attributes.name,
    email: attributes.email,
    attributes: attributes as unknown as UserEmailAttributes,
  };
};
